import {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
} from "react";
import { IconButton } from "@/components/common/IconButton";
import { formatTimecode } from "./formatTime";
import { toLabelHighlightRuns, type LabelMatch } from "./labelSearch";
import type { SegmentRange } from "./useSegmentation";

const COLLAPSED_LINES = 1;
const LABEL_LINE_HEIGHT = 20;
const LABEL_PADDING_X = 6;
/** 一致件数バッジの行の高さ。丸みのある小片の寸法であって本文の行送りではない。 */
const MATCH_BADGE_LINE_HEIGHT = 16;
const LABEL_PADDING_Y = 4;
const COLLAPSED_MAX_HEIGHT = COLLAPSED_LINES * LABEL_LINE_HEIGHT + LABEL_PADDING_Y * 2;

interface SegmentCardProps {
	index: number;
	segment: SegmentRange;
	label: string;
	matches: readonly LabelMatch[];
	activeMatch: LabelMatch | null;
	expanded: boolean;
	selected: boolean;
	hovered: boolean;
	onLabelChange: (index: number, label: string) => void;
	onToggleExpanded: (index: number) => void;
	onSeek: (index: number, start: number) => void;
	onHover: (index: number | null) => void;
}

const textStyle: CSSProperties = {
	boxSizing: "border-box",
	width: "100%",
	padding: `${LABEL_PADDING_Y}px ${LABEL_PADDING_X}px`,
	fontFamily: "var(--font-sans)",
	fontSize: "var(--text-ui)",
	lineHeight: `${LABEL_LINE_HEIGHT}px`,
	whiteSpace: "pre-wrap",
	overflowWrap: "anywhere",
	overflow: "hidden",
};

export const SegmentCard = memo(function SegmentCard({
	index,
	segment,
	label,
	matches,
	activeMatch,
	expanded,
	selected,
	hovered,
	onLabelChange,
	onToggleExpanded,
	onSeek,
	onHover,
}: SegmentCardProps) {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const labelBodyRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const runs = useMemo(
		() => toLabelHighlightRuns(label, matches, activeMatch),
		[label, matches, activeMatch],
	);
	const resizeTextarea = useCallback(() => {
		const textarea = textareaRef.current;
		if (!textarea) return;
		textarea.style.height = "auto";
		const height = textarea.scrollHeight;
		textarea.style.height = `${height}px`;
		setIsOverflowing(height > COLLAPSED_MAX_HEIGHT);
	}, []);

	useLayoutEffect(() => {
		const body = labelBodyRef.current;
		if (!body) return;
		let lastObservedWidth = body.getBoundingClientRect().width;
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry || entry.contentRect.width === lastObservedWidth) return;
			lastObservedWidth = entry.contentRect.width;
			resizeTextarea();
		});
		observer.observe(body);
		return () => observer.disconnect();
	}, [resizeTextarea]);

	useLayoutEffect(() => {
		resizeTextarea();
	}, [label, resizeTextarea]);

	useEffect(() => {
		if (!expanded) textareaRef.current?.blur();
	}, [expanded]);

	const headerStyle: CSSProperties = {
		display: "flex",
		alignItems: "center",
		gap: 6,
		minWidth: 0,
	};
	const hasLabelControls = matches.length > 0 || isOverflowing;
	const labelTextStyle: CSSProperties = {
		...textStyle,
		paddingRight:
			matches.length > 0 && isOverflowing ? 72 : hasLabelControls ? 36 : LABEL_PADDING_X,
	};

	return (
		<div
			className={`group my-2 cursor-pointer rounded-4 px-2 py-1.5 transition-colors ${hovered ? "bg-row-selected-bg" : selected ? "bg-surface-2" : "bg-surface-1 hover:bg-row-selected-bg"}`}
			style={{ boxShadow: selected ? "var(--shadow-ring-strong)" : "var(--shadow-ring)" }}
			onClick={() => onSeek(index, segment.start)}
			onMouseEnter={() => onHover(index)}
			onMouseLeave={() => onHover(null)}
			data-testid={`segment-card-${index}`}
		>
			<div className="opacity-60" style={headerStyle}>
				{/* 番号は色ではなく数字で区間を指す。色は選択中だけに付ける (ホバー中はカードの地で示す)。 */}
				<span
					className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-2 text-section font-mono ${selected ? "bg-accent-solid text-on-accent-solid" : "bg-neutral-solid text-on-neutral-solid"}`}
				>
					{index + 1}
				</span>
				<span className="shrink-0 text-section font-mono text-fg-3">
					{formatTimecode(segment.start, 1)} - {formatTimecode(segment.end, 1)}
				</span>
			</div>
			<div
				ref={labelBodyRef}
				className="relative mt-0.5 w-full overflow-hidden rounded-3"
				style={{ maxHeight: !expanded && isOverflowing ? COLLAPSED_MAX_HEIGHT : undefined }}
				onClick={(event) => {
					// ラベル編集中のキャレット移動で再生位置が先頭へ戻らないよう、選択済みならシークしない。
					event.stopPropagation();
					if (!selected) onSeek(index, segment.start);
				}}
			>
				<div
					className="pointer-events-none absolute inset-0 overflow-hidden text-transparent"
					style={labelTextStyle}
					data-testid={`segment-label-highlights-${index}`}
				>
					{runs.map((run, runIndex) => (
						<span
							key={`${run.state}-${runIndex}`}
							style={
								run.state === "plain"
									? undefined
									: {
											backgroundColor:
												run.state === "active"
													? "var(--search-match-active-bg)"
													: "var(--search-match-bg)",
										}
							}
						>
							{run.text}
						</span>
					))}
				</div>
				<textarea
					ref={textareaRef}
					rows={1}
					value={label}
					onChange={(event) => onLabelChange(index, event.target.value)}
					onFocus={() => {
						if (!expanded) onToggleExpanded(index);
					}}
					placeholder={`セグメント${index + 1}のラベル…`}
					className="relative z-10 block w-full resize-none border-0 bg-transparent text-fg-1 outline-none placeholder:text-fg-3"
					style={labelTextStyle}
					data-testid={`segment-label-${index}`}
				/>
				{hasLabelControls && (
					<div className="pointer-events-none absolute right-1 top-1 z-20 flex items-center gap-1">
						{matches.length > 0 && (
							<span
								className="shrink-0 rounded-full px-1.5 font-mono text-section"
								style={{
									background: "var(--accent-subtle)",
									color: "var(--accent)",
									lineHeight: `${MATCH_BADGE_LINE_HEIGHT}px`,
								}}
							>
								{matches.length}件
							</span>
						)}
						{isOverflowing && (
							<IconButton
								icon={expanded ? "chevronD" : "chevronR"}
								iconSize={14}
								size={22}
								className="pointer-events-auto shrink-0"
								color="var(--fg-2)"
								onPointerDown={(event) => event.stopPropagation()}
								onClick={(event) => {
									event.stopPropagation();
									onToggleExpanded(index);
								}}
								data-testid={`segment-label-toggle-${index}`}
							/>
						)}
					</div>
				)}
			</div>
		</div>
	);
});
